/**
 * Open-Meteo client (SPEC 2.5.1). No API key. Free tier.
 *
 * Cached at the HTTP layer via a 30-min in-memory map keyed by lat,lon
 * rounded to 0.1° (~10km bins) — keeps requests sparse on rapid re-renders.
 */

export type WeatherSnapshot = {
  current: {
    temperature_c: number;
    apparent_temperature_c: number;
    weather_code: number;
    wind_speed_kmh: number;
    is_day: boolean;
    time: string; // ISO
  };
  today: {
    temperature_min_c: number;
    temperature_max_c: number;
    precipitation_probability_max: number;
    weather_code: number;
    sunrise: string;
    sunset: string;
  };
  resolved_at: string; // ISO when this snapshot was fetched
  source: "open_meteo";
  location: { lat: number; lon: number };
};

const TTL_MS = 30 * 60 * 1000;
const cache = new Map<string, { ts: number; value: WeatherSnapshot }>();

function key(lat: number, lon: number): string {
  return `${lat.toFixed(1)},${lon.toFixed(1)}`;
}

export async function fetchWeather(
  lat: number,
  lon: number,
): Promise<WeatherSnapshot> {
  const k = key(lat, lon);
  const hit = cache.get(k);
  const now = Date.now();
  if (hit && now - hit.ts < TTL_MS) return hit.value;

  const url = new URL("https://api.open-meteo.com/v1/forecast");
  url.searchParams.set("latitude", String(lat));
  url.searchParams.set("longitude", String(lon));
  url.searchParams.set(
    "current",
    "temperature_2m,apparent_temperature,weather_code,wind_speed_10m,is_day",
  );
  url.searchParams.set(
    "daily",
    "temperature_2m_min,temperature_2m_max,precipitation_probability_max,weather_code,sunrise,sunset",
  );
  url.searchParams.set("timezone", "auto");
  url.searchParams.set("forecast_days", "1");

  const r = await fetch(url, { signal: AbortSignal.timeout(8000) });
  if (!r.ok) throw new Error(`Open-Meteo HTTP ${r.status}`);
  const d = await r.json();

  const snapshot: WeatherSnapshot = {
    current: {
      temperature_c: d.current.temperature_2m,
      apparent_temperature_c: d.current.apparent_temperature,
      weather_code: d.current.weather_code,
      wind_speed_kmh: d.current.wind_speed_10m,
      is_day: d.current.is_day === 1,
      time: d.current.time,
    },
    today: {
      temperature_min_c: d.daily.temperature_2m_min[0],
      temperature_max_c: d.daily.temperature_2m_max[0],
      precipitation_probability_max:
        d.daily.precipitation_probability_max?.[0] ?? 0,
      weather_code: d.daily.weather_code[0],
      sunrise: d.daily.sunrise[0],
      sunset: d.daily.sunset[0],
    },
    resolved_at: new Date().toISOString(),
    source: "open_meteo",
    location: { lat, lon },
  };
  cache.set(k, { ts: now, value: snapshot });
  return snapshot;
}

/**
 * WMO weather code → short label + glyph (very compressed — full mapping is
 * https://open-meteo.com/en/docs#weathervariables but the spec only renders
 * a glyph + temp, so this is enough.
 */
export function weatherLabel(code: number, isDay: boolean): { label: string; glyph: string } {
  if (code === 0) return { label: "Clear", glyph: isDay ? "☀" : "☾" };
  if (code <= 2) return { label: "Mostly clear", glyph: isDay ? "🌤" : "🌙" };
  if (code === 3) return { label: "Overcast", glyph: "☁" };
  if (code >= 45 && code <= 48) return { label: "Fog", glyph: "🌫" };
  if (code >= 51 && code <= 57) return { label: "Drizzle", glyph: "🌦" };
  if (code >= 61 && code <= 67) return { label: "Rain", glyph: "🌧" };
  if (code >= 71 && code <= 77) return { label: "Snow", glyph: "🌨" };
  if (code >= 80 && code <= 82) return { label: "Showers", glyph: "🌦" };
  if (code >= 85 && code <= 86) return { label: "Snow showers", glyph: "🌨" };
  if (code >= 95 && code <= 99) return { label: "Thunder", glyph: "⛈" };
  return { label: `Code ${code}`, glyph: "·" };
}
